import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { Input } from '../../ui/Input';
import { Button } from '../../ui/Button';
import { Alert } from '../../ui/Alert';
import GoldenCustomerCard from '../../ui/GoldenCustomerCard';
import CustomerForm from '../../customers/CustomerFormPanel';
import { customerAPI, unwrapList } from '../../../services/api';
import { prefillFromCustomer, emptyPrefill } from '../../../quote-builder';
import { useConfirm } from '../../ui/ConfirmationModal';

/**
 * CustomerBlock — Quote builder customer section (PR-3).
 *
 * Three-state wrapper that replaces the customer portion of QuoteHeader:
 *
 *   1. EMPTY   — typeahead search input + "+ Create <name>" affordance.
 *                Same behavior as the previous QuoteHeader search — preserved
 *                per non-goal in PR-3 §7.
 *
 *   2. CARD    — <GoldenCustomerCard variant="preview" /> + Edit CTA.
 *                Also renders a small "Clear" button so the user can back out
 *                to search a different customer.
 *
 *   3. FORM    — <CustomerForm /> — Add (from search "+ Create") or Edit
 *                (from card's Edit CTA). Cancel returns to the previous state
 *                with data intact (per PR-3 §5 acceptance).
 *
 * The parent (QuoteBuilderPage / QuoteHeader) owns `prefill` — CustomerBlock
 * mutates it via `onPrefillChange` to stay consistent with the existing
 * prefill model.
 */

const MODE = {
  EMPTY: 'empty',
  CARD: 'card',
  FORM: 'form',
};

// IDI-167 (2026-09-02): the customer typeahead (seed-on-focus AND search
// results) was neither sorted nor filtered to active customers -- both
// paths render whatever order the backend happened to return (arbitrary;
// CompanyIndex has no sort key), including inactive customers a user should
// not be re-selecting onto a NEW quote. Confirmed live: 960 real inactive
// customer records exist in bidit-dev-customers. Applied to both call
// sites so they agree by construction.
function sortAndFilterActiveCustomers(list) {
  return (Array.isArray(list) ? list : [])
    .filter((c) => (c?.status || 'active') !== 'inactive')
    .slice()
    .sort((a, b) => {
      const nameA = (a?.name || a?.company || '').toLowerCase();
      const nameB = (b?.name || b?.company || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
}

// Convert a customer record (as returned by the API) into a shape
// GoldenCustomerCard consumes. We reuse the raw record because the card is
// permissive about field naming (contacts / addresses / salespeople all fall
// back to variants).
function customerFromPrefill(prefill, fullCustomer) {
  if (fullCustomer) return fullCustomer;
  if (!prefill?.customerId && !prefill?.isNewCustomer) return null;
  // Synthesize a display-only customer from the prefill state (used when we
  // have no fetched record yet — e.g. an in-quote just-created customer that
  // came back from the API on save).
  return {
    id: prefill.customerId,
    name: prefill.customerName,
    company: prefill.company,
    customer_type: prefill.customerType,
    status: prefill.status || 'active',
    contacts: prefill.allContacts?.length
      ? prefill.allContacts
      : (prefill.contact ? [{ ...prefill.contact, is_primary: true }] : []),
    addresses: [
      prefill.billingAddress && (prefill.billingAddress.line1 || prefill.billingAddress.zip)
        ? { ...prefill.billingAddress, is_primary: true }
        : null,
      !prefill.sameAsBilling && prefill.serviceAddress && (prefill.serviceAddress.line1 || prefill.serviceAddress.zip)
        ? { ...prefill.serviceAddress, is_primary: false, label: 'Service' }
        : null,
    ].filter(Boolean),
    salespeople: prefill.salespersons || [],
    payment_terms: prefill.paymentTerm,
    payment_type: prefill.paymentType,
  };
}

export default function CustomerBlock({
  prefill,
  onPrefillChange,
  // IDI-652: the dealer's Quote Defaults term, for a customer with none.
  dealerDefaultPaymentTerm,
}) {
  // ─── Initial mode derives from prefill ──────────────────────────────────
  const confirm = useConfirm();
  const [mode, setMode] = useState(() => (
    prefill?.customerId ? MODE.CARD : (prefill?.isNewCustomer ? MODE.FORM : MODE.EMPTY)
  ));
  const [fullCustomer, setFullCustomer] = useState(null);

  // When the async prefill arrives with a customerId and we are still in
  // EMPTY mode (edit-mode path: parent mounts empty then setPrefill()s after
  // /v1/quotes/:id resolves), promote to CARD. We do NOT auto-leave CARD/FORM
  // when customerId clears — handleClear is the only path back to EMPTY.
  useEffect(() => {
    if (prefill?.customerId && mode === MODE.EMPTY) {
      setMode(MODE.CARD);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill?.customerId]);

  // ─── Typeahead search (unchanged behavior from QuoteHeader) ─────────────
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (!customerSearch || customerSearch.length < 2) {
      // §4 (293_): seed results on focus with an empty query — the placeholder
      // promises "Search customers or create new…" so an empty focus should show something.
      if (showDropdown && (!customerSearch || customerSearch.length === 0)) {
        setIsSearching(true);
        // IDI-100: getCustomers({ limit }) returns the enveloped shape
        // { customers: [...], has_more, next_cursor } once a limit is
        // passed — `res?.data || res` never matched it and silently
        // rendered "No customers found" against a real, populated tenant.
        //
        // IDI-167 (2026-09-02): the pre-search seed list was neither
        // sorted nor filtered to active customers, so "first 20 by however
        // DynamoDB happened to scan them" (arbitrary — CompanyIndex has no
        // sort key) could easily be all-inactive or miss anyone whose name
        // starts late in the alphabet. Sort + active-filter applied via
        // sortAndFilterActiveCustomers below, same as the search-results
        // path, so the seed list and search results agree by construction.
        customerAPI.getCustomers({ limit: 100 }).then((res) => {
          const { list } = unwrapList(res, 'customers', 'CustomerBlock.getCustomers(limit)');
          setCustomerResults(sortAndFilterActiveCustomers(list).slice(0, 20));
        }).catch(() => setCustomerResults([])).finally(() => setIsSearching(false));
        return;
      }
      setCustomerResults([]);
      setShowDropdown(false);
      return;
    }
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        // IDI-100 note corrected (IDI-167, 2026-09-02): the backend DOES
        // filter by `search` today (customerMatchesSearch in
        // cmd/customers/main.go, shipped after this comment was written) —
        // matches name/company/email/phone/first_name/last_name and
        // per-contact name/email, case-insensitive substring. The earlier
        // claim that this call "returns ALL customers unfiltered" is now
        // stale and was actively misleading (it made the real, separate
        // "search doesn't filter" symptom look intentional/known-broken
        // rather than a live regression worth checking against current code).
        const res = await customerAPI.getCustomers({ search: customerSearch });
        const { list } = unwrapList(res, 'customers', 'CustomerBlock.getCustomers(search)');
        setCustomerResults(sortAndFilterActiveCustomers(list));
        setShowDropdown(true);
      } catch {
        setCustomerResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch, showDropdown]);

  // ─── When we land in CARD mode with a customerId but no full record,
  //     fetch the full customer so the card renders faithfully. ──────────
  useEffect(() => {
    let ignore = false;
    if (mode === MODE.CARD && prefill?.customerId && !fullCustomer) {
      customerAPI.getCustomer(prefill.customerId)
        .then((res) => {
          if (ignore) return;
          const data = res?.data ?? res;
          setFullCustomer(data);
          // DEF-136: sync prefill.customerName with the authoritative customer
          // record so the denormalised name on the quote stays current.
          // Also sync allContacts so the writeback doesn't overwrite with [].
          const updates = {};
          if (data?.name && data.name !== prefill.customerName) {
            updates.customerName = data.name;
          }
          if (Array.isArray(data?.contacts) && data.contacts.length > 0 &&
              (!prefill.allContacts || prefill.allContacts.length === 0)) {
            updates.allContacts = data.contacts.map(c => ({
              id: c.id,
              name: c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim(),
              firstName: c.first_name || c.name?.split(' ')[0] || '',
              lastName: c.last_name || c.name?.split(' ').slice(1).join(' ') || '',
              role: c.role || c.title || '',
              email: c.email || '',
              phone: c.phone || c.mobile || '',
              isPrimary: c.isPrimary || c.is_primary || false,
            }));
            // Also sync the primary contact
            const primary = data.contacts.find(c => c.is_primary || c.isPrimary) || data.contacts[0];
            if (primary && !prefill.contact) {
              updates.contact = {
                id: primary.id,
                name: primary.name || `${primary.first_name || ''} ${primary.last_name || ''}`.trim(),
                role: primary.role || primary.title || '',
                email: primary.email || '',
                phone: primary.phone || primary.mobile || '',
              };
            }
          }
          if (Object.keys(updates).length > 0) {
            onPrefillChange(prev => ({ ...prev, ...updates }));
          }
        })
        .catch(() => { /* card will fall back to prefill-synthesized shape */ });
    }
    // fullCustomer gates the one-shot fetch. prefill.customerName/allContacts/contact
    // are read only as write-back guards (would loop if added to deps).
    // onPrefillChange uses the functional updater form so no stale-closure risk.
    // Same intent as the sibling effect above (line ~87).
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, prefill?.customerId, fullCustomer]);

  // ─── Handlers ────────────────────────────────────────────────────────────

  const handleSelectExistingCustomer = useCallback(async (customer) => {
    setShowDropdown(false);
    setCustomerSearch('');
    try {
      const res = await customerAPI.getCustomer(customer.id);
      const data = res?.data ?? res;
      setFullCustomer(data);
      onPrefillChange(prefillFromCustomer(data, { defaultPaymentTerm: dealerDefaultPaymentTerm }));
    } catch {
      setFullCustomer(customer);
      onPrefillChange(prefillFromCustomer(customer, { defaultPaymentTerm: dealerDefaultPaymentTerm }));
    }
    setMode(MODE.CARD);
  }, [onPrefillChange, dealerDefaultPaymentTerm]);

  const handleStartAddNew = useCallback(() => {
    setShowDropdown(false);
    setMode(MODE.FORM);
  }, []);

  const handleClear = useCallback(async () => {
    // PR-24 §1 (297_): confirm before clearing — removing the customer also
    // resets customerDefaultMargin (Lindsey L2) and customerType (L3), which
    // can silently re-price a hand-priced quote. Line items are kept.
    // Only prompt when there is a customer to lose.
    if (prefill?.customerId || fullCustomer?.id) {
      const ok = await confirm({
        title: 'Remove this customer?',
        message: "This clears the contact, addresses, payment terms and the customer's margin defaults from this quote. Line items are kept.",
        confirmLabel: 'Remove customer',
        tone: 'danger',
      });
      if (!ok) return;
    }
    setFullCustomer(null);
    setCustomerSearch('');
    onPrefillChange(emptyPrefill());
    setMode(MODE.EMPTY);
  }, [onPrefillChange, confirm, prefill?.customerId, fullCustomer?.id]);

  const handleEdit = useCallback(() => {
    setMode(MODE.FORM);
  }, []);

  const handleFormCancel = useCallback(() => {
    // Cancel from FORM returns to the previous state with data intact:
    //   • if we already had a customer (edit path) → back to CARD
    //   • otherwise (add path) → back to EMPTY
    if (prefill?.customerId || fullCustomer?.id) {
      setMode(MODE.CARD);
    } else {
      setMode(MODE.EMPTY);
    }
  }, [prefill?.customerId, fullCustomer?.id]);

  const handleFormSave = useCallback((savedCustomer) => {
    setFullCustomer(savedCustomer);
    onPrefillChange(prefillFromCustomer(savedCustomer, { defaultPaymentTerm: dealerDefaultPaymentTerm }));
    setMode(MODE.CARD);
  }, [onPrefillChange, dealerDefaultPaymentTerm]);

  // ─── Render ──────────────────────────────────────────────────────────────

  const displayCustomer = customerFromPrefill(prefill, fullCustomer);

  return (
    <section className="space-y-3" aria-label="Customer">
      {/* ─── EMPTY: search input + dropdown ─── */}
      {mode === MODE.EMPTY && (
        <div className="relative">
          <Input
            label="Customer"
            value={customerSearch}
            onChange={(e) => setCustomerSearch(e.target.value)}
            onFocus={() => setShowDropdown(true)}
            placeholder="Search customers or create new…"
            autoComplete="off"
            aria-label="Customer search"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={showDropdown}
            aria-controls="customer-search-listbox"
          />
          {showDropdown && (
            <div
              className="absolute z-40 mt-1 w-full rounded-md border bg-white py-1"
              style={{ borderColor: 'var(--color-border)', maxHeight: '16rem', overflowY: 'auto' }}
              role="listbox" id="customer-search-listbox"
            >
              {isSearching && (
                <div className="px-3 py-2 text-sm text-gray-500">Searching…</div>
              )}
              {!isSearching && customerResults.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-500">No customers found</div>
              )}
              {customerResults.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  role="option"
                  aria-selected="false"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between"
                  onClick={() => handleSelectExistingCustomer(c)}
                >
                  <span className="font-medium">{c.name || c.company_name}</span>
                  {c.city && <span className="text-gray-400 text-xs">{c.city}, {c.state}</span>}
                </button>
              ))}
              <button
                type="button"
                className="w-full text-left px-3 py-2 text-sm font-medium border-t"
                style={{ color: 'var(--color-action-primary)' }}
                onClick={handleStartAddNew}
              >
                + Add new customer{customerSearch ? ` "${customerSearch}"` : ''}
              </button>
            </div>
          )}
          {/* Always-visible fallback for the add-new affordance so users can
              start a fresh customer without typing a search query first. */}
          {!showDropdown && (
            <div className="mt-2">
              <button
                type="button"
                onClick={handleStartAddNew}
                className="text-xs font-medium underline"
                style={{ color: 'var(--color-action-primary)' }}
              >
                + Add new customer
              </button>
            </div>
          )}
        </div>
      )}

      {/* PT-53A W3.2 (DEF-125): surface customer load failure. */}
      {prefill?.customerLoadError && (
        <Alert variant="warning" className="mb-2">
          {prefill.customerLoadError}
        </Alert>
      )}

      {/* ─── CARD: preview card + Edit CTA ─── */}
      {mode === MODE.CARD && displayCustomer && (
        <div className="relative">
          <GoldenCustomerCard
            customer={displayCustomer}
            surface="quote"
            variant="preview"
            onEdit={handleEdit}
            actions={
              <>
                {/* PT-14 C1 (Wave 1, 2026-07-24): Edit + Remove are proper
                    bordered buttons, not text links, per the round-2 audit. */}
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleEdit}
                  aria-label="Edit customer"
                >
                  <PencilSquareIcon className="h-4 w-4" /> Edit
                </Button>
                {/* PR-16 (2026-07-22): renamed "Clear" → "Remove" to match wire
                    APP-02 preview variant — makes the affordance obvious. */}
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleClear}
                  aria-label="Remove customer"
                >
                  <XMarkIcon className="h-4 w-4" /> Remove
                </Button>
              </>
            }
          />
        </div>
      )}

      {/* ─── FORM: add or edit ─── */}
      {mode === MODE.FORM && (
        <CustomerForm
          initialData={fullCustomer || (prefill?.customerId ? customerFromPrefill(prefill, null) : null)}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
        />
      )}
    </section>
  );
}

// Test-only exports
export { MODE, customerFromPrefill, sortAndFilterActiveCustomers };
